import {
  MutableRefObject,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { UseFormReturn, useForm } from 'react-hook-form';

import {
  ButtonTextProps,
  StringIndexedObject,
  useToast,
} from '@kanda-libs/ks-design-library';
import {
  SatNote,
  Service,
  actions,
  services,
  useSubmit,
} from '@kanda-libs/ks-frontend-services';
import {
  SatNoteOpenBankingRequestBodyStruct,
  SatNotePdfResponse,
} from '@kanda-libs/ks-frontend-services/src/service/external';

import { useAppDispatch } from 'components/App';
import useApiError from 'hooks/useApiError';
import { reloadInitialData } from 'store/slices/app';
import { scrollToTop } from 'utils';

import { formatOpenBankingSatNoteBody, getFileName } from './helpers';

import { DEFAULT_NEXT_BUTTON_PROPS } from '../../constants';
import { SatNotePage, useOpenBankingSatNoteContext } from '../../context';

export interface FingerprintBoolean {
  value: boolean;
  timestamp: number;
  ip: string;
}

export interface SatNoteFormQuestions {
  acceptedTerms: boolean;
  acknowledged: FingerprintBoolean;
  job: {
    sat_note: Pick<SatNote, 'q_pressure' | 'q_easiness'>;
  };
}

export interface OpenBankingSatNoteFormHook {
  form: UseFormReturn<SatNoteFormQuestions>;
  onSubmit: (data: StringIndexedObject<SatNoteFormQuestions>) => void;
  page: SatNotePage;
  isLoading: boolean;
  isSubmitting: boolean;
  nextButtonProps: ButtonTextProps;
  scrollRef: MutableRefObject<HTMLDivElement | null>;
}

export default function useOpenBankingSatNoteForm(): OpenBankingSatNoteFormHook {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const { job, companyInfo, page, toNextPage, isLoading } =
    useOpenBankingSatNoteContext();
  const { showError } = useToast();

  const dispatch = useAppDispatch();
  const onSignError = useApiError('Error signing sat note at this time');
  const { submit: createSatNotePdf } = useSubmit(
    services.satnotev2.openbanking as unknown as Service<
      SatNotePdfResponse,
      object,
      SatNoteOpenBankingRequestBodyStruct
    >,
  );

  const form = useForm<SatNoteFormQuestions>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  const acceptedTerms = form.watch('acceptedTerms');
  const acknowledgedField = form.watch('acknowledged');
  const acknowledged = acknowledgedField?.value;

  const disabled = useMemo(() => {
    if (isLoading || isSubmitting) return true;
    if (page === 'terms' && !acceptedTerms) return true;
    if (page === 'questions' && !acknowledged) return true;
    return false;
  }, [isLoading, acceptedTerms, acknowledged, page, isSubmitting]);

  const nextButtonProps = useMemo(
    () => ({
      ...DEFAULT_NEXT_BUTTON_PROPS,
      ...(page === 'questions' && {
        label: 'Submit',
        submit: true,
      }),
      ...(page !== 'questions' && {
        onClick: () =>
          setTimeout(() => {
            toNextPage();
            if (!scrollRef.current) {
              scrollToTop();
              return;
            }
            const element = scrollRef.current;
            element.scrollIntoView({
              behavior: 'smooth',
            });
          }, 10),
      }),
      id: `${DEFAULT_NEXT_BUTTON_PROPS.id}-${page}`,
      disabled,
    }),
    [page, toNextPage, disabled],
  );

  const onSubmit = useCallback(
    (formValues: StringIndexedObject<SatNoteFormQuestions>) => {
      if (!job || !companyInfo) {
        showError(
          'Job not available - please refresh and try again. If this error persists, please contact Ideal4Finance',
        );
        return;
      }
      const body = formatOpenBankingSatNoteBody(
        formValues as unknown as SatNoteFormQuestions,
        job,
        companyInfo,
      );

      if (!body.satNote || !body.fingerprint.value) {
        showError(
          'Error encountered with form field values - please refresh and try again. If this error persists, please contact Ideal4Finance',
        );
        return;
      }
      setIsSubmitting(true);
      createSatNotePdf({
        body: {
          ...body,
        },
      }).then(({ data: pdf, error: pdfError }) => {
        if (pdfError) {
          showError(
            typeof pdfError === 'string'
              ? pdfError
              : 'Error generating sat note at this time',
          );
          return;
        }
        if (!pdf || !pdf?.base64) {
          showError('Unknown error generating sat note at this time');
          return;
        }
        dispatch(
          actions.signJobSatNote({
            params: {
              id: job.id as string,
            },
            body: {
              ...body.satNote,
              certificate: {
                name: getFileName(job, 'openbanking-sat-note'),
                content: pdf.base64,
              },
              signature: {
                signed: 'yes',
                fingerprint: body.fingerprint.ip,
              },
            },
            onError: (error) => {
              onSignError(error);
              setIsSubmitting(false);
            },
            onSuccess: () => {
              setIsSubmitting(false);
              reloadInitialData();
            },
          }),
        );
      });
    },
    [createSatNotePdf, dispatch, job, companyInfo, onSignError, showError],
  );

  return {
    form,
    onSubmit,
    page,
    isLoading,
    isSubmitting,
    scrollRef,
    nextButtonProps,
  };
}
